import React, { useState, useEffect } from 'react';
import { Check, AlertCircle, Loader2, Eye, EyeOff, Building2, Mail, User as UserIcon, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Invitation {
  id: string;
  email: string;
  name: string;
  role: string;
  companyName: string;
  inviterName: string;
  expiresAt: Date;
}

export function AcceptInvitePage() {
  const [token, setToken] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extrair token da URL
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('token');
    
    if (!inviteToken) {
      setError('Link de convite inválido');
      setLoading(false);
      return;
    }

    setToken(inviteToken);
    loadInvitation(inviteToken);
  }, []);

  const loadInvitation = async (inviteToken: string) => {
    try {
      console.log('🔍 Buscando convite:', inviteToken);

      const { data, error: fetchError } = await supabase
        .from('user_invitations')
        .select(`
          id,
          email,
          name,
          role,
          expires_at,
          status,
          tenants!inner(name),
          invited_by_user:users!user_invitations_invited_by_fkey(name)
        `)
        .eq('token', inviteToken)
        .single();

      if (fetchError || !data) {
        setError('Convite não encontrado');
        setLoading(false);
        return;
      }

      // Verificar status
      if (data.status === 'accepted') {
        setError('Este convite já foi aceito');
        setLoading(false);
        return;
      }

      if (data.status === 'cancelled') {
        setError('Este convite foi cancelado');
        setLoading(false);
        return;
      }

      // Verificar expiração
      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) {
        setError('Este convite expirou. Entre em contato com o administrador para solicitar um novo convite.');
        setLoading(false);
        return;
      }

      setInvitation({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        companyName: data.tenants.name,
        inviterName: data.invited_by_user.name,
        expiresAt
      });

      setLoading(false);
    } catch (err) {
      console.error('❌ Erro ao carregar convite:', err);
      setError('Erro ao carregar convite');
      setLoading(false);
    }
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (!invitation || !token) return;

    setAccepting(true);

    try {
      // 1. Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.email,
        password: password,
        options: {
          data: {
            name: invitation.name,
            full_name: invitation.name
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Erro ao criar usuário');

      // 2. Aceitar convite (atualizar role e tenant_id do usuário)
      const { data: acceptData, error: acceptError } = await supabase.rpc(
        'accept_user_invitation',
        {
          p_token: token,
          p_user_id: authData.user.id
        }
      );

      if (acceptError) throw acceptError;

      const result = acceptData as { success: boolean; error?: string };
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao aceitar convite');
      }

      setSuccess(true);

      // Redirecionar após 3 segundos
      setTimeout(() => {
        window.location.href = '/user-dashboard';
      }, 3000);

    } catch (err) {
      console.error('❌ Erro ao aceitar convite:', err);
      setError(err instanceof Error ? err.message : 'Erro ao aceitar convite');
      setAccepting(false);
    }
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'manager':
        return 'Administrador';
      case 'user':
        return 'Usuário';
      case 'owner':
        return 'Proprietário';
      default:
        return role;
    }
  };

  const getTimeRemaining = () => {
    if (!invitation) return '';
    
    const now = new Date();
    const diff = invitation.expiresAt.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando convite...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Convite Inválido</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-medium"
          >
            Voltar para o Login
          </a>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Convite Aceito!</h2>
          <p className="text-gray-600 mb-2">
            Sua conta foi criada com sucesso.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Redirecionando para a plataforma...
          </p>
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Main accept form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white text-center">
          <img 
            src="/space-logo-black.png" 
            alt="Space AI" 
            className="h-12 mx-auto mb-4 filter brightness-0 invert"
          />
          <h1 className="text-3xl font-bold mb-2">Você foi convidado! 🎉</h1>
          <p className="text-blue-100">Complete seu cadastro para começar</p>
        </div>

        {/* Invitation Info */}
        <div className="p-8 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <Building2 className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Empresa</p>
                <p className="text-sm font-semibold text-gray-900">{invitation?.companyName}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <UserIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Convidado por</p>
                <p className="text-sm font-semibold text-gray-900">{invitation?.inviterName}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Sua função</p>
                <p className="text-sm font-semibold text-gray-900">{invitation && getRoleName(invitation.role)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-semibold text-gray-900">{invitation?.email}</p>
              </div>
            </div>
          </div>

          {/* Time Remaining */}
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            <p className="text-xs text-yellow-800">
              <strong>Convite expira em:</strong> {getTimeRemaining()}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleAccept} className="p-8 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Crie sua senha</h3>
            
            {/* Password */}
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha *
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={accepting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Senha *
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Digite a senha novamente"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={accepting}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            disabled={accepting}
          >
            {accepting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Criando conta...</span>
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>Aceitar Convite e Criar Conta</span>
              </>
            )}
          </button>

          {/* Info */}
          <p className="text-center text-xs text-gray-500">
            Ao aceitar o convite, você concorda com os{' '}
            <a href="/terms" className="text-blue-600 hover:underline">
              Termos de Uso
            </a>{' '}
            e{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">
              Política de Privacidade
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}

